import React, { useEffect, useMemo, useState } from "react";
import { Alert } from "@mui/material";
import TelegramIcon from "@mui/icons-material/Telegram";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { useLanguage } from "../i18n/LanguageContext";
import SiteFooter, { TELEGRAM_HANDLE, TELEGRAM_URL } from "../components/SiteFooter";
import TurnstileWidget from "../components/TurnstileWidget";
import { mascotSrc } from "../utils/mascotBus";
import { captureRef, clearRef, writeRef } from "../utils/refCode";

const AuthPage = ({ embedded = false, initialMode = "login", onDone }) => {
  const { login, login2fa, register } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const [dialogMode, setDialogMode] = useState(initialMode === "register" ? "register" : "login");
  const mode = embedded ? dialogMode : location.pathname.includes("register") ? "register" : "login";
  const expired = params.get("expired") === "1";
  const replaced = params.get("replaced") === "1";
  const refFromLink = captureRef(params.toString());

  const [loginForm, setLoginForm] = useState({ login: "", password: "", code: "" });
  const [regForm, setRegForm] = useState({ name: "", phone: "", username: "", email: "", password: "", ref: refFromLink });
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileKey, setTurnstileKey] = useState(0);
  const [pending, setPending] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const resetTurnstile = () => {
    setTurnstileToken("");
    setTurnstileKey((value) => value + 1);
  };

  useEffect(() => {
    const next = captureRef(params.toString());
    if (!next) return;
    setRegForm((prev) => (prev.ref ? prev : { ...prev, ref: next }));
  }, [params]);

  const switchMode = (next) => {
    if (next === mode) return;
    setError("");
    setPending("");
    resetTurnstile();
    if (embedded) {
      setDialogMode(next);
      return;
    }
    const kept = writeRef(regForm.ref) || captureRef(params.toString());
    navigate(next === "register" ? (kept ? `/register?ref=${encodeURIComponent(kept)}` : "/register") : "/login", { replace: true });
  };

  const finish = () => {
    if (embedded) {
      onDone?.();
      return;
    }
    navigate("/");
  };

  const onLoginChange = (event) => {
    setLoginForm((prev) => ({ ...prev, [event.target.name]: event.target.value }));
  };

  const onRegChange = (event) => {
    const { name, value } = event.target;
    setRegForm((prev) => ({ ...prev, [name]: value }));
    if (name === "ref") writeRef(value);
  };

  const submitLogin = async (event) => {
    event.preventDefault();
    setError("");
    if (!pending && !turnstileToken) {
      setError(t("auth.captcha"));
      return;
    }
    setBusy(true);
    try {
      if (pending) {
        await login2fa({ pendingToken: pending, code: loginForm.code });
        finish();
        return;
      }
      const data = await login({
        login: loginForm.login,
        password: loginForm.password,
        turnstileToken,
      });
      if (data.require2fa) {
        setPending(data.pendingToken);
        return;
      }
      finish();
    } catch (err) {
      setError(err.message);
      resetTurnstile();
    } finally {
      setBusy(false);
    }
  };

  const submitRegister = async (event) => {
    event.preventDefault();
    setError("");
    if (!turnstileToken) {
      setError(t("auth.captcha"));
      return;
    }
    setBusy(true);
    try {
      await register({
        ...regForm,
        ref: String(regForm.ref || "").trim(),
        turnstileToken,
      });
      clearRef();
      finish();
    } catch (err) {
      setError(err.message);
      resetTurnstile();
    } finally {
      setBusy(false);
    }
  };

  const turnstileBox = useMemo(
    () => (
      <TurnstileWidget
        key={`auth-ts-${turnstileKey}-${mode}`}
        resetKey={turnstileKey}
        onVerify={(token) => setTurnstileToken(token || "")}
        onExpire={() => setTurnstileToken("")}
        onError={() => setTurnstileToken("")}
      />
    ),
    [turnstileKey, mode]
  );

  const mascotLine = error
    ? "Bir şeyler ters gitti, tekrar dene."
    : mode === "register"
      ? "Kayıt ol, hemen oynamaya başla!"
      : pending
        ? "Authenticator kodunu gir."
        : "Tekrar hoş geldin!";
  const mascotImg = error ? "sad" : mode === "register" ? "tease" : pending ? "ok" : "hello";

  const authMascot = (
    <div className={`auth-mascot${error ? " mood-sad" : mode === "register" ? " mood-tease" : pending ? " mood-ok" : ""}`}>
      <span className="auth-mascot-glow" aria-hidden />
      <img src={mascotSrc(mascotImg)} alt="" />
      <em>{mascotLine}</em>
    </div>
  );

  const card = (
    <div className={`auth-card${embedded ? " embedded" : ""}`}>
          <div className="auth-tabs">
              <span className={`auth-tab-glider${mode === "register" ? " is-register" : ""}`} aria-hidden />
              <button type="button" className={mode === "login" ? "on" : ""} onClick={() => switchMode("login")}>
                {t("auth.login")}
              </button>
              <button type="button" className={mode === "register" ? "on" : ""} onClick={() => switchMode("register")}>
                {t("auth.register")}
              </button>
            </div>
          {authMascot}
          {expired ? <Alert severity="warning" sx={{ mb: 2 }}>{t("auth.sessionExpired")}</Alert> : null}
          {replaced ? <Alert severity="info" sx={{ mb: 2 }}>{t("auth.sessionReplaced")}</Alert> : null}
          {error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : null}

          <div className={`auth-slider is-${mode}`}>
            <div className="auth-slider-track">
              <form className="auth-pane" onSubmit={submitLogin}>
                <h1>{t("auth.loginTitle")}</h1>
                <p className="lead">{pending ? t("auth.login2fa") : t("auth.loginLead")}</p>
                {pending ? (
                  <label className="auth-field">
                    <span>2FA kodu</span>
                    <input className="auth-input" name="code" value={loginForm.code} onChange={onLoginChange} autoComplete="one-time-code" />
                  </label>
                ) : (
                  <>
                    <label className="auth-field">
                      <span>Kullanıcı adı / e-posta</span>
                      <input className="auth-input" name="login" value={loginForm.login} onChange={onLoginChange} autoComplete="username" />
                    </label>
                    <label className="auth-field">
                      <span>Şifre</span>
                      <input className="auth-input" name="password" type="password" value={loginForm.password} onChange={onLoginChange} autoComplete="current-password" />
                    </label>
                    {mode === "login" && !pending ? turnstileBox : null}
                  </>
                )}
                <button type="submit" className="auth-submit" disabled={busy || (!pending && !turnstileToken)}>
                  {busy && mode === "login" ? "Kontrol ediliyor..." : pending ? "Doğrula" : t("auth.login")}
                </button>
              </form>

              <form className="auth-pane" onSubmit={submitRegister}>
                <h1>{t("auth.registerTitle")}</h1>
                <p className="lead">{t("auth.registerLead")}</p>
                <label className="auth-field">
                  <span>Ad soyad</span>
                  <input className="auth-input" name="name" value={regForm.name} onChange={onRegChange} autoComplete="name" />
                </label>
                <label className="auth-field">
                  <span>GSM numarası</span>
                  <input className="auth-input" name="phone" value={regForm.phone} onChange={onRegChange} autoComplete="tel" placeholder="05xx xxx xx xx" />
                </label>
                <label className="auth-field">
                  <span>Kullanıcı adı</span>
                  <input className="auth-input" name="username" value={regForm.username} onChange={onRegChange} autoComplete="username" />
                </label>
                <label className="auth-field">
                  <span>E-posta</span>
                  <input className="auth-input" name="email" type="email" value={regForm.email} onChange={onRegChange} autoComplete="email" />
                </label>
                <label className="auth-field">
                  <span>Şifre</span>
                  <input className="auth-input" name="password" type="password" value={regForm.password} onChange={onRegChange} autoComplete="new-password" />
                </label>
                <label className="auth-field">
                  <span>{t("auth.refCode")}</span>
                  <input
                    className="auth-input"
                    name="ref"
                    value={regForm.ref}
                    onChange={onRegChange}
                    autoComplete="off"
                    placeholder="ABC123"
                    maxLength={24}
                    spellCheck={false}
                  />
                  <small className="auth-field-hint">{t("auth.refCodeHint")}</small>
                </label>
                {mode === "register" ? turnstileBox : null}
                <button type="submit" className="auth-submit" disabled={busy || !turnstileToken}>
                  {busy && mode === "register" ? "Oluşturuluyor..." : t("auth.register")}
                </button>
              </form>
            </div>
          </div>
    </div>
  );

  if (embedded) return card;

  return (
    <div className="auth-page">
      <div className="auth-stage">
        {card}

        <aside className="auth-visual">
          <img className="auth-visual-art" src="/auth-hero.png?v=3" alt="" />
          <img className="auth-mascot-side" src={mascotSrc("hello")} alt="" />
        </aside>
      </div>
      <a className="auth-telegram" href={TELEGRAM_URL} target="_blank" rel="noreferrer">
        <TelegramIcon fontSize="small" />
        {TELEGRAM_HANDLE}
      </a>
      <SiteFooter compact />
    </div>
  );
};

export default AuthPage;
